import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  AirRingColors,
  RingPriceData,
  isRingAirColorDisabled,
} from 'content/products/ring/productMap/constants';
import { Currency } from 'utils/Currency';
import {
  getRingBuyPageFaqData,
  getRingBuyPageFaqData_AE,
  getRingBuyPageFaqData_IN,
} from 'content/products/ring/faqData';
import {
  generateProductSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
  toResolvedFaqItems,
} from 'lib/seo/schema';
import { extractTextFromJSX } from 'utils/extractTextFromJSX';
import { getAssetUrl } from 'lib/config/assetUrls';
import { usSeoImagePath } from 'lib/seo/meta/seoImage';

type JsonLdObject = Record<string, unknown>;

type TFunction = (key: string) => string;

interface RingPricingSchemaParams {
  locale: string;
  country: string;
  /** Path slug for canonical URLs; pass from RegionLocaleContext so ROW locales are not `/row/...`. */
  regionSlug?: string;
  store: ShopifyStore;
  currency: Currency;
  defaultColor?: AirRingColors | null;
  t?: TFunction;
}

export const getRingPricingSchemas = ({
  locale,
  country,
  regionSlug,
  store,
  currency,
  defaultColor,
  t,
}: RingPricingSchemaParams): JsonLdObject[] => {
  const localeUpper = (locale || 'us').toUpperCase();
  const localeLower = localeUpper.toLowerCase();
  const countryLower = (country || localeLower).toLowerCase();
  const countryUpper = countryLower.toUpperCase();
  const urlSlug = (regionSlug || localeLower).toLowerCase();

  const actualStore = store;
  const actualCurrency = currency;

  const color: AirRingColors =
    defaultColor && !isRingAirColorDisabled(defaultColor, actualStore)
      ? defaultColor
      : AirRingColors.RAW_TITANIUM;

  const priceData =
    actualStore && color
      ? RingPriceData[actualStore as ShopifyStore]?.[color as AirRingColors]
      : null;

  const actualPrice = priceData?.price ?? 349;
  const currencyString = actualCurrency.toUpperCase();

  // Use a fallback identity function if t is not provided (for SSR/schema generation)
  const defaultT: TFunction = (key) => key;
  const translation = t || defaultT;
  const faqSource =
    actualStore === ShopifyStore.AE
      ? getRingBuyPageFaqData_AE(translation)
      : countryUpper === 'IN'
      ? getRingBuyPageFaqData_IN(translation)
      : getRingBuyPageFaqData(translation);

  const productSchema = generateProductSchema({
    name: 'Ultrahuman Ring AIR',
    description:
      "Ultrahuman Ring AIR is the world's most comfortable and compact smart ring that monitors your sleep, movement and recovery.",
    imageUrl: getAssetUrl(
      usSeoImagePath(countryUpper, '/web_v2/backgrounds/ring-air-hero.png'),
    ),
    brand: 'Ultrahuman',
    category: 'Wearable Technology',
    price: actualPrice,
    currency: currencyString,
    country: countryUpper,
    availability: 'InStock',
    url: `https://www.ultrahuman.com/${urlSlug}/ring/buy/`,
    rating: {
      ratingValue: 4.7,
      reviewCount: 2500,
    },
  });

  const faqItems = toResolvedFaqItems(
    faqSource.map((faq) => ({
      question: extractTextFromJSX(faq.title) || '',
      answer: extractTextFromJSX(faq.description) || '',
    })),
  );

  const faqSchema = generateFAQSchema(faqItems);

  const breadcrumbSchema = generateBreadcrumbSchema([
    {
      name: 'Home',
      url: 'https://www.ultrahuman.com',
    },
    {
      name: 'Ultrahuman Ring',
      url: 'https://www.ultrahuman.com/ring',
    },
    {
      name: 'Buy Ring AIR',
      url: `https://www.ultrahuman.com/${urlSlug}/ring/buy/`,
    },
  ]);

  return [productSchema, faqSchema, breadcrumbSchema];
};
